<div class="operation-details-page">
  <div class="page-header">
    <app-back-button
      [parentRoute]="parentRoute"
      [useHistoryBack]="true"
      [title]="pageTitleKey"
      [titleLink]="assignmentProcessUrl"
      [subtitle]="pageSubtitle"
    >
      @if (showAssignmentProcessKeyLink) {
        <div class="record-process-key">
          <app-copyable-identifier
            [value]="assignmentProcessKey"
            [link]="assignmentProcessUrl"
          ></app-copyable-identifier>
          @if (assignmentProcessVersion != null) {
            <span class="record-process-version">v{{ assignmentProcessVersion }}</span>
          }
        </div>
      }
      @if (executedOnLabel) {
        <div class="record-executed-on">
          <span>{{ 'Executed on' | translate }}: </span>
          <app-assignment-host-label
            [serviceName]="executedOnService"
            [hostName]="executedOnHost"
            [workerId]="executedOnWorkerId"
            [fallback]="executedOnLabel"
          ></app-assignment-host-label>
        </div>
      }
    </app-back-button>
    <div class="header-actions">
      @if (canRestartCurrent) {
        <app-secondary-button
          title="Restart assignment"
          icon="pi pi-replay"
          size="small"
          [disable]="detailsLoading"
          (action)="requestRestart()"
        ></app-secondary-button>
      }
      @if (canCancelCurrent) {
        <app-secondary-button
          title="Cancel assignment"
          icon="pi pi-stop-circle"
          size="small"
          [disable]="detailsLoading"
          (action)="requestCancellation()"
        ></app-secondary-button>
      }
      @if (selectedAssignment) {
        <app-secondary-button
          title="Make subscription"
          icon="pi pi-calendar-plus"
          size="small"
          [disable]="detailsLoading"
          (action)="requestMakeSubscription()"
        ></app-secondary-button>
        <app-secondary-button
          title="Notify people"
          icon="pi pi-bell"
          size="small"
          [disable]="detailsLoading"
          (action)="requestNotifyPeople()"
        ></app-secondary-button>
        <app-secondary-button
          title="Copy assignment"
          icon="copy"
          size="small"
          [disable]="detailsLoading"
          (action)="requestCopy()"
        ></app-secondary-button>
      }
      <app-secondary-button
        title="Refresh data"
        icon="refresh"
        size="small"
        [disable]="detailsLoading"
        [loading]="detailsLoading"
        (action)="refreshDetails()"
      ></app-secondary-button>
    </div>
  </div>
  @if (errorMessage) {
    <div class="error-message">{{ errorMessage | translate }}</div>
  }
  @if (detailsLoading && !selectedAssignment && !selectedJob) {
    <app-loading [loading]="true"></app-loading>
  } @else {
    @if (selectedAssignment || selectedJob) {
      <app-tabs
        [tabs]="detailsTabs"
        [activeTab]="activeDetailsTab"
        (tabChanged)="onDetailsTabChange($event)"
      ></app-tabs>
    }
  }

<ng-template #requestStepsTemplate>
    <section class="request-steps-panel">
      @if (requestStepTree.length) {
        <div class="request-steps-split">
          <div class="request-steps-tree">
            <ul class="external-call-tree">
              @for (command of requestStepTree; track command.id) {
                <ng-container *ngTemplateOutlet="requestTreeNode; context: { $implicit: command }" />
              }
            </ul>
            <ng-template #requestTreeNode let-node>
              <li>
                <button
                  type="button"
                  class="tree-row"
                  [class.has-calls]="node.hasCalls"
                  [class.is-external]="node.isExternal"
                  [class.open]="selectedRowId === node.id"
                  (click)="selectRequestTreeRow(node)">
                  @if (node.children.length) {
                    <i
                      class="pi"
                      [class.pi-chevron-down]="node.expanded"
                      [class.pi-chevron-right]="!node.expanded"
                      (click)="toggleRequestTreeNode(node, $event)"></i>
                  } @else {
                    <i class="pi pi-minus"></i>
                  }
                  @if (node.httpMethod) {
                    <code>{{ node.httpMethod }}</code>
                  }
                  <span class="tree-title" [title]="node.title">
                    @if (node.kind === 'command' && node.isUnassigned) {
                      {{ 'Unassigned command' | translate }}
                    } @else {
                      {{ node.title }}
                    }
                    @if (node.kind === 'command' && node.commandTypeShort) {
                      <span class="tree-command-type">{{ node.commandTypeShort }}</span>
                    }
                  </span>
                  @if (node.kind === 'command') {
                    <p-tag [value]="'Command' | translate" [severity]="node.hasCalls ? 'info' : 'secondary'"></p-tag>
                  } @else if (node.kind === 'method') {
                    <p-tag [value]="'Method' | translate" severity="info"></p-tag>
                  } @else {
                    <p-tag [value]="'External request' | translate" severity="info"></p-tag>
                  }
                  @if (node.kind === 'command' && node.state !== '—') {
                    <p-tag [value]="node.state | translate" [severity]="node.severity"></p-tag>
                  }
                  @if (node.kind === 'command' && node.attempt !== '—') {
                    <span class="tree-meta">{{ 'Attempt' | translate }} {{ node.attempt }}</span>
                  }
                  @if (node.kind === 'command' && node.startedAt) {
                    <span class="tree-meta">{{ node.startedAt | dateFormatLang:translate.currentLang:'seconds' }}</span>
                  }
                  @if (node.kind === 'command' && node.duration !== '—') {
                    <span class="tree-meta">{{ node.duration }}</span>
                  }
                  @if (node.kind === 'command' && node.workerId !== '—') {
                    <app-copyable-identifier [value]="node.workerId"></app-copyable-identifier>
                  }
                  @if (node.statusLabel) {
                    <p-tag [value]="node.statusLabel" [severity]="node.severity"></p-tag>
                  }
                  @if (node.durationLabel) {
                    <span class="tree-meta">{{ node.durationLabel }}</span>
                  }
                  @if (node.kind === 'http') {
                    <span class="tree-meta">{{ node.proxyLabel || ('Direct' | translate) }}</span>
                  }
                </button>
                @if (node.expanded && node.children.length) {
                  <ul>
                    @for (child of node.children; track child.id) {
                      <ng-container *ngTemplateOutlet="requestTreeNode; context: { $implicit: child }" />
                    }
                  </ul>
                }
              </li>
            </ng-template>
          </div>
          <aside class="request-steps-detail">
            @if (callDetailsLoading) {
              <app-loading [loading]="true"></app-loading>
            } @else if (selectedCallIo) {
              <div class="call-io-stack">
                @if (selectedCallIo.error) {
                  <div class="error-message compact">{{ selectedCallIo.error }}</div>
                }
                <p><strong>{{ 'Proxy' | translate }}:</strong> {{ selectedCallIo.proxyLabel }}</p>
                <app-copyable-log-field
                  [label]="'HTTP request'"
                  [text]="selectedCallIo.requestText"
                ></app-copyable-log-field>
                <app-copyable-log-field
                  [label]="'HTTP response'"
                  [text]="selectedCallIo.responseText"
                ></app-copyable-log-field>
              </div>
            } @else if (selectedCommandDetail) {
              <div class="call-io-stack">
                @if (selectedCommandDetail.commandTypeShort) {
                  <button
                    type="button"
                    class="command-type-copy"
                    [class.copied]="copiedCommandType === selectedCommandDetail.commandTypeShort"
                    [attr.title]="(copiedCommandType === selectedCommandDetail.commandTypeShort ? 'Copied' : 'Copy command type') | translate"
                    (click)="copyCommandType(selectedCommandDetail.commandTypeShort)"
                  >
                    <strong>{{ selectedCommandDetail.commandTypeShort }}</strong>
                    <i
                      class="pi"
                      [class.pi-check]="copiedCommandType === selectedCommandDetail.commandTypeShort"
                      [class.pi-copy]="copiedCommandType !== selectedCommandDetail.commandTypeShort"
                      aria-hidden="true"
                    ></i>
                  </button>
                }
                <div class="command-detail-meta">
                  @if (selectedCommandDetail.attempt !== '—') {
                    <span>{{ 'Attempt' | translate }} {{ selectedCommandDetail.attempt }}</span>
                  }
                  @if (selectedCommandDetail.startedAt) {
                    <span>{{ selectedCommandDetail.startedAt | dateFormatLang:translate.currentLang:'seconds' }}</span>
                  }
                  @if (selectedCommandDetail.duration !== '—') {
                    <span>{{ selectedCommandDetail.duration }}</span>
                  }
                  @if (selectedCommandDetail.workerId !== '—') {
                    <app-copyable-identifier [value]="selectedCommandDetail.workerId"></app-copyable-identifier>
                  }
                </div>
                @if (selectedCommandDetail.error) {
                  <div class="error-message compact">{{ selectedCommandDetail.error }}</div>
                }
                @if (selectedCommandDetail.satelliteProductUrl) {
                  <div>
                    <span>{{ 'Satellite product ID' | translate }}</span>
                    <app-copyable-identifier
                      [value]="selectedCommandDetail.satelliteProductId || ''"
                      [link]="selectedCommandDetail.satelliteProductUrl"
                    ></app-copyable-identifier>
                  </div>
                }
                <app-copyable-log-field
                  [label]="'Command input'"
                  [text]="selectedCommandDetail.inputJson"
                ></app-copyable-log-field>
                <app-copyable-log-field
                  [label]="'Command output'"
                  [text]="selectedCommandDetail.resultJson"
                ></app-copyable-log-field>
                @if (selectedCommandDetail.logs.length) {
                  <app-copyable-log-field
                    [label]="'Command execution log'"
                    [text]="selectedCommandDetail.logText"
                  ></app-copyable-log-field>
                } @else {
                  <div>
                    <h4>{{ 'Command execution log' | translate }}</h4>
                    <p class="empty-state compact">{{ 'No command execution logs' | translate }}</p>
                  </div>
                }
              </div>
            } @else {
              <p class="empty-state compact">{{ 'Select a request step' | translate }}</p>
            }
          </aside>
        </div>
      } @else {
        <p class="empty-state compact">{{ 'No command executions found' | translate }}</p>
      }
    </section>
</ng-template>

<ng-template #overviewTemplate>
    @if (selectedAssignment; as assignment) {
    <section class="assignment-summary">
      <div class="assignment-summary-title">
        <div>
          <h2>
            <app-copyable-identifier
              [value]="assignmentProcessKey"
              [link]="assignmentProcessUrl"
            ></app-copyable-identifier>
            @if (assignmentProcessVersion != null) {
              <span class="record-process-version">v{{ assignmentProcessVersion }}</span>
            }
          </h2>
          <p class="muted">
            <app-copyable-identifier [value]="assignment.id || ''"></app-copyable-identifier>
          </p>
        </div>
        <p-tag [value]="assignmentStateLabel | translate" [severity]="assignmentStateSeverity"></p-tag>
      </div>
      <div class="details-grid">
        <div><span>{{ 'Initiator' | translate }}</span><strong>{{ (assignment.initiatorKind ?? '—') | translate }}</strong></div>
        <div>
          <span>{{ 'Initiator ID' | translate }}</span>
          <app-copyable-identifier [value]="assignment.initiatorId || ''"></app-copyable-identifier>
        </div>
        <div><span>{{ 'Channel' | translate }}</span><strong>{{ (assignment.channel ?? '—') | translate }}</strong></div>
        <div><span>{{ 'Execution mode' | translate }}</span><strong>{{ (assignment.executionMode ?? '—') | translate }}</strong></div>
        <div>
          <span>{{ 'Correlation ID' | translate }}</span>
          <app-copyable-identifier [value]="assignment.correlationId || ''"></app-copyable-identifier>
        </div>
        <div>
          <span>{{ 'Process subscription' | translate }}</span>
          <app-copyable-identifier
            [value]="assignment.sourceSubscriptionId || ''"
            [link]="sourceSubscriptionUrl"
          ></app-copyable-identifier>
        </div>
        <div><span>{{ 'Started At' | translate }}</span><strong>{{ assignment.startedAt ? (assignment.startedAt | dateFormatLang:translate.currentLang:'seconds') : '—' }}</strong></div>
        <div><span>{{ 'Completed At' | translate }}</span><strong>{{ assignment.completedAt ? (assignment.completedAt | dateFormatLang:translate.currentLang:'seconds') : '—' }}</strong></div>
        @if (satelliteProductAnalysisUrl) {
          <div>
            <span>{{ 'Satellite product ID' | translate }}</span>
            <app-copyable-identifier
              [value]="satelliteProductId || ''"
              [link]="satelliteProductAnalysisUrl"
            ></app-copyable-identifier>
          </div>
        }
      </div>
      @if (onnxModel) {
        <h3>{{ 'ONNX model' | translate }}</h3>
        @if (onnxModel.id || onnxModel.code) {
          <div class="details-grid">
            <div>
              <span>{{ 'Name' | translate }}</span>
              @if (onnxModel.url && onnxModel.name) {
                <a class="process-key-link" [routerLink]="onnxModel.url"><strong>{{ onnxModel.name }}</strong></a>
              } @else if (onnxModel.url) {
                <app-copyable-identifier [value]="onnxModel.id || ''" [link]="onnxModel.url"></app-copyable-identifier>
              } @else {
                <strong>{{ onnxModel.name || onnxModel.code || '—' }}</strong>
              }
            </div>
            <div><span>{{ 'Code' | translate }}</span><strong>{{ onnxModel.code || '—' }}</strong></div>
            <div><span>{{ 'Version' | translate }}</span><strong>{{ onnxModel.version || '—' }}</strong></div>
            <div><span>{{ 'ONNX file' | translate }}</span><strong>{{ onnxModel.fileName || '—' }}</strong></div>
            @if (onnxModel.id && onnxModel.name) {
              <div>
                <span>{{ 'Id' | translate }}</span>
                <app-copyable-identifier [value]="onnxModel.id" [link]="onnxModel.url"></app-copyable-identifier>
              </div>
            }
          </div>
        } @else {
          <p class="empty-state compact">{{ 'ONNX model was not recorded' | translate }}</p>
        }
      }
      @if (assignmentInputJson) {
        <app-json-tree
          [label]="'Input parameters'"
          [text]="assignmentInputJson"
        ></app-json-tree>
      } @else {
        <div>
          <h3>{{ 'Input parameters' | translate }}</h3>
          <p class="empty-state compact">{{ 'No input parameters' | translate }}</p>
        </div>
      }
      @if (assignmentResultJson) {
        <app-json-tree
          [label]="'Assignment result'"
          [text]="assignmentResultJson"
        ></app-json-tree>
      } @else {
        <div>
          <h3>{{ 'Assignment result' | translate }}</h3>
          <p class="empty-state compact">{{ 'No assignment result' | translate }}</p>
        </div>
      }
    </section>
    <section class="assignment-chain">
      <h3>{{ 'Created artifacts' | translate }}</h3>
      @if (databaseLoading && !createdArtifactFiles.length && !createdArtifactTables.length) {
        <app-loading [loading]="true"></app-loading>
      } @else if (createdArtifactFiles.length || createdArtifactTables.length) {
        <ul class="chain-list">
          @for (file of createdArtifactFiles; track file.id) {
            <li>
              <span class="chain-kind">{{ file.bucket }}</span>
              @if (file.downloadUrl) {
                <a class="storage-file-link" [href]="file.downloadUrl" [attr.download]="file.path">{{ file.path }}</a>
              } @else {
                <span>{{ file.path }}</span>
              }
              <span class="muted">{{ file.bytesLabel }}</span>
            </li>
          }
          @for (table of createdArtifactTables; track table.key) {
            <li>
              <span class="chain-kind">{{ table.table }}</span>
              <strong>{{ table.count }}</strong>
              <span class="muted">{{ table.bytesLabel }}</span>
              @for (product of table.products; track product.id) {
                <app-copyable-identifier [value]="product.id" [link]="product.url"></app-copyable-identifier>
              }
            </li>
          }
        </ul>
      } @else {
        <p class="empty-state compact">{{ 'No artifacts written yet' | translate }}</p>
      }
    </section>
    <section class="assignment-chain">
      <h3>{{ 'Follow-up assignments' | translate }}</h3>
      @if (followUpAssignments.length) {
        <ol class="chain-list">
          @for (item of followUpAssignments; track item.eventId + item.targetAssignmentId) {
            <li>
              @if (item.eventUrl) {
                <a class="process-key-link" [routerLink]="item.eventUrl">
                  <span class="chain-kind">{{ item.eventKey }}</span>
                </a>
              } @else {
                <span class="chain-kind">{{ item.eventKey }}</span>
              }
              <app-copyable-identifier [value]="item.targetAssignmentId" [link]="item.targetAssignmentUrl"></app-copyable-identifier>
              @if (item.state !== '—') {
                <p-tag [value]="item.state | translate" [severity]="item.severity"></p-tag>
              }
            </li>
          }
        </ol>
      } @else {
        <p class="empty-state compact">{{ 'No follow-up assignments' | translate }}</p>
      }
    </section>
    <section class="assignment-chain">
      <div class="section-heading">
        <h3>{{ 'Assignment chain' | translate }}</h3>
        @if (childOperations.length) {
          <app-secondary-button
            title="Show in monitoring"
            size="small"
            (action)="emitShowChildren(assignment.id)"
          ></app-secondary-button>
        }
      </div>
      <ol class="chain-list">
        @for (link of assignmentChain; track link.kind + link.id) {
          <li [class.current]="link.kind === 'current'">
            <span class="chain-kind">{{ link.title | translate }}</span>
            @if (link.kind === 'current') {
              @if (link.detailUrl) {
                <a class="process-key-link" [routerLink]="link.detailUrl"><strong>{{ link.detail }}</strong></a>
              } @else {
                <strong>{{ link.detail }}</strong>
              }
              <app-copyable-identifier [value]="link.id"></app-copyable-identifier>
            } @else {
              <app-copyable-identifier [value]="link.id" [link]="link.url"></app-copyable-identifier>
            }
            @if (link.hostLabel) {
              <span class="chain-host">
                <app-assignment-host-label
                  [serviceName]="link.workerServiceName"
                  [hostName]="link.workerHostName"
                  [workerId]="link.workerId"
                  [fallback]="link.hostLabel"
                ></app-assignment-host-label>
              </span>
            }
            @if (link.state !== '—') {
              <p-tag [value]="link.state | translate" [severity]="link.severity"></p-tag>
            }
          </li>
        }
      </ol>
    </section>

    @if (isPostProcessingAssignment) {
      <h3>{{ 'Post-processing pipeline' | translate }}</h3>
      <div class="process-flow" [attr.data-state]="assignment.state">
        <div><i class="pi pi-file-import"></i><strong>{{ 'Parse' | translate }}</strong></div>
        <i class="pi pi-arrow-right flow-arrow"></i>
        <div><i class="pi pi-image"></i><strong>{{ 'Preview' | translate }}</strong></div>
        <i class="pi pi-arrow-right flow-arrow"></i>
        <div><i class="pi pi-chart-line"></i><strong>{{ 'Index' | translate }}</strong></div>
      </div>
    }

    @if (satelliteParameterRows.length) {
      <h3>{{ 'Satellite workflow parameters' | translate }}</h3>
      <div class="parameters-grid">
        @for (parameter of satelliteParameterRows; track parameter.label) {
          <div><span>{{ parameter.label | translate }}</span><strong>{{ parameter.value }}</strong></div>
        }
        <div class="parameter-regions">
          <span>{{ 'Regions' | translate }}</span>
          @if (satelliteRegions.length > 0) {
            <button class="id-link" type="button" (click)="toggleSatelliteRegions()">
              <strong>{{ satelliteRegions.length }}</strong>
            </button>
            @if (satelliteRegionsExpanded) {
              <ul class="region-list">
                @for (region of satelliteRegions; track region.id) {
                  <li>
                    <strong>{{ region.name }}</strong>
                    @if (region.name !== region.id) {
                      <app-copyable-identifier [value]="region.id"></app-copyable-identifier>
                    }
                  </li>
                }
              </ul>
            }
          } @else {
            <strong>{{ satelliteRegionCount }}</strong>
          }
        </div>
      </div>
    }

    <h3>{{ 'State history' | translate }}</h3>
    <div class="timeline">
      @for (transition of historyViews; track transition.id) {
        <div class="timeline-item">
          <div class="timeline-marker"></div>
          <div>
            <div class="timeline-title">
              <p-tag [value]="transition.state | translate" [severity]="transition.severity"></p-tag>
              <span>{{ transition.changedAt | dateFormatLang:translate.currentLang:'seconds' }}</span>
            </div>
            @if (transition.details) {
              <p>{{ transition.details }}</p>
            }
          </div>
        </div>
      }
    </div>

    @if (assignment.lastError) {
      <div class="error-message">{{ assignment.lastError }}</div>
    }
    }
    @if (selectedJob; as job) {
      <div class="details-grid">
        <div><span>{{ 'Status' | translate }}</span><p-tag [value]="(job.state ?? '—') | translate" [severity]="jobStateSeverity"></p-tag></div>
        <div><span>{{ 'Initiator' | translate }}</span><strong>{{ 'Scheduler' | translate }}</strong></div>
        <div><span>{{ 'Channel' | translate }}</span><strong>{{ 'Quartz' | translate }}</strong></div>
        <div><span>{{ 'Operation type' | translate }}</span><strong>{{ job.jobName }}</strong></div>
        <div>
          <span>{{ 'Correlation ID' | translate }}</span>
          <app-copyable-identifier [value]="job.fireInstanceId || ''"></app-copyable-identifier>
        </div>
        <div><span>{{ 'Started At' | translate }}</span><strong>{{ job.startedAt | dateFormatLang:translate.currentLang:'seconds' }}</strong></div>
        <div><span>{{ 'Completed At' | translate }}</span><strong>{{ job.completedAt ? (job.completedAt | dateFormatLang:translate.currentLang:'seconds') : '—' }}</strong></div>
        <div><span>{{ 'Job group' | translate }}</span><strong>{{ job.jobGroup }}</strong></div>
        <div><span>{{ 'Trigger' | translate }}</span><strong>{{ job.triggerGroup }}/{{ job.triggerName }}</strong></div>
      </div>
      @if (job.error) {
        <div class="error-message">{{ job.error }}</div>
      }
      @if (job.safeConfigJson) {
        <app-copyable-log-field
          [label]="'Config at job start'"
          heading="h3"
          [text]="jobConfigJson"
        ></app-copyable-log-field>
      }
      @if (job.resultJson) {
        @if (jobResultTypeName) {
          <p class="muted">
            <app-copyable-identifier [value]="jobResultTypeName"></app-copyable-identifier>
          </p>
        }
        <app-copyable-log-field
          [label]="'Job result'"
          heading="h3"
          [text]="jobResultJson"
        ></app-copyable-log-field>
      }
    }
</ng-template>

<ng-template #notificationsTemplate>
  <section class="orch-table-panel">
    <app-orchestration-table-heading heading="Event notifications">
      <p class="muted">{{ 'Notifications for this assignment' | translate }}</p>
      <div tableActions class="table-actions">
        <app-secondary-button
          title="Refresh"
          icon="refresh"
          [disable]="notificationsLoading"
          [loading]="notificationsLoading"
          (action)="loadAssignmentNotifications()"
        ></app-secondary-button>
      </div>
    </app-orchestration-table-heading>
    <app-data-table
      [columns]="notificationColumns"
      [data]="notificationRows"
      [config]="notificationTableConfig"
      [loading]="notificationsLoading"
    ></app-data-table>
  </section>
</ng-template>

<ng-template #eventsTemplate>
  <section class="orch-table-panel">
    <app-orchestration-table-heading
      heading="Published events"
      [(query)]="eventSearch"
    >
      <p class="muted">{{ 'Events this assignment published' | translate }}</p>
    </app-orchestration-table-heading>
    <div class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>{{ 'Kind' | translate }}</th>
            <th>{{ 'Event' | translate }}</th>
            <th>{{ 'Identifier' | translate }}</th>
            <th>{{ 'Trigger' | translate }}</th>
            <th>{{ 'Status' | translate }}</th>
            <th>{{ 'Started At' | translate }}</th>
            <th>{{ 'Completed At' | translate }}</th>
            <th>{{ 'Target assignment' | translate }}</th>
          </tr>
        </thead>
        <tbody>
          @for (event of visibleEventViews; track event.id) {
            <tr>
              <td>{{ event.kind | translate }}</td>
              <td>
                @if (event.eventUrl) {
                  <app-copyable-identifier [value]="event.eventKey" [link]="event.eventUrl"></app-copyable-identifier>
                } @else {
                  <app-copyable-identifier [value]="event.eventKey"></app-copyable-identifier>
                }
                @if (event.parametersJson) {
                  <pre class="inline-json"><code>{{ event.parametersJson }}</code></pre>
                }
                @if (event.error) {
                  <div class="error-message compact">{{ event.error }}</div>
                }
              </td>
              <td>
                @if (event.eventId) {
                  <span class="muted">{{ event.identifierLabel | translate }}</span>
                  <app-copyable-identifier
                    [value]="event.eventId"
                    [link]="event.productAnalysisUrl"
                  ></app-copyable-identifier>
                } @else {
                  <span>—</span>
                }
              </td>
              <td>{{ event.triggerName }}</td>
              <td><p-tag [value]="event.state | translate" [severity]="event.severity"></p-tag></td>
              <td>{{ event.startedAt | dateFormatLang:translate.currentLang:'seconds' }}</td>
              <td>{{ event.completedAt ? (event.completedAt | dateFormatLang:translate.currentLang:'seconds') : '—' }}</td>
              <td>
                @if (event.targetAssignmentId) {
                  <app-copyable-identifier
                    [value]="event.targetAssignmentId"
                    [link]="event.targetAssignmentUrl"
                  ></app-copyable-identifier>
                } @else {
                  <span>—</span>
                }
              </td>
            </tr>
          } @empty {
            <tr><td colspan="8" class="empty-state">{{ 'No published events found' | translate }}</td></tr>
          }
        </tbody>
      </table>
    </div>
  </section>
</ng-template>

<ng-template #databaseTemplate>
  <section class="database-panel">
    @if (selectedJob && !selectedAssignment) {
      <p class="empty-state compact">{{ 'Database records are available for assignments' | translate }}</p>
    } @else {
      <p class="database-hint">{{ 'Database records hint' | translate }}</p>
      @if (satelliteDownloadInProgress) {
        <p class="database-hint">{{ 'Satellite product downloading hint' | translate }}</p>
      }
      <div class="orch-table-panel">
      <app-orchestration-table-heading heading="Expected database actions" [(query)]="dataEffectSearch">
      </app-orchestration-table-heading>
      <app-data-table
        [columns]="dataEffectColumns"
        [data]="visibleDataEffectRows"
        [config]="dataEffectTableConfig"
        [loading]="databaseLoading"
      ></app-data-table>
      </div>
      <div class="orch-table-panel">
      <app-orchestration-table-heading heading="Records written by this assignment" [(query)]="dataRecordSearch">
        <p>{{ 'Total size' | translate }}: {{ dataRecordBytesLabel }}</p>
      </app-orchestration-table-heading>
      <div class="orch-filter-toolbar database-record-filters">
        <div class="orch-filter-toolbar__group">
          <msf-form-builder
            [fields]="dataRecordFilterFields"
            [(model)]="dataRecordFilterModel"
            flexDirection="row"
            (modelChange)="applyDataRecordFilters()"
          ></msf-form-builder>
        </div>
        <div class="orch-filter-toolbar__group">
          <app-primary-button
            title="Apply"
            size="small"
            (action)="applyDataRecordFilters()"
          ></app-primary-button>
          <app-clear-button (clear)="clearDataRecordFilters()"></app-clear-button>
        </div>
      </div>
      <app-data-table
        [columns]="dataRecordColumns"
        [data]="visibleDataRecordRows"
        [config]="dataRecordTableConfig"
        [loading]="databaseLoading"
      ></app-data-table>
      </div>
      <p class="database-hint">{{ 'Object storage actions hint' | translate }}</p>
      <div class="orch-table-panel">
      <app-orchestration-table-heading heading="Expected object-storage actions" [(query)]="storageEffectSearch">
      </app-orchestration-table-heading>
      <app-data-table
        [columns]="storageEffectColumns"
        [data]="visibleStorageEffectRows"
        [config]="storageEffectTableConfig"
        [loading]="databaseLoading"
      ></app-data-table>
      </div>
      <div class="orch-table-panel">
      <app-orchestration-table-heading heading="Objects written by this assignment" [(query)]="storageRecordSearch">
        <p>{{ 'Total size' | translate }}: {{ storageBytesLabel }}</p>
      </app-orchestration-table-heading>
      <app-data-table
        [columns]="storageRecordColumns"
        [data]="visibleStorageRecordRows"
        [config]="storageRecordTableConfig"
        [loading]="databaseLoading"
      ></app-data-table>
      </div>
    }
  </section>
</ng-template>

<ng-template #dataEffectCommandTemplate let-row>
  <app-copyable-identifier [value]="row.commandLabel" [link]="row.commandUrl"></app-copyable-identifier>
</ng-template>

<ng-template #dataEffectOperationTemplate let-row>
  <p-tag [value]="row.operation | translate" [severity]="row.operationSeverity"></p-tag>
</ng-template>

<ng-template #dataEffectDescriptionTemplate let-row>
  <span>{{ row.description | translate }}</span>
</ng-template>

<ng-template #storageRecordPathTemplate let-row>
  @if (row.downloadUrl) {
    <a class="storage-file-link" [href]="row.downloadUrl" [attr.download]="row.path || ''">{{ row.path || row.id }}</a>
  } @else {
    <span>{{ row.path || '—' }}</span>
  }
</ng-template>

<ng-template #dataRecordIdTemplate let-row>
  <app-copyable-identifier [value]="row.id || ''" [link]="row.fileDownloadUrl || row.productAnalysisUrl || null"></app-copyable-identifier>
</ng-template>

<ng-template #dataRecordKindTemplate let-row>
  <p-tag [value]="row.kind | translate" [severity]="row.kind === 'domain' ? 'info' : 'secondary'"></p-tag>
</ng-template>

<ng-template #dataRecordStateTemplate let-row>
  @if (row.state) {
    <div class="status-with-duration">
      <p-tag [value]="(row.stateLabel || row.state) | translate" [severity]="row.stateSeverity"></p-tag>
      <small>
        <app-live-status-duration
          [changedAt]="row.changedAt"
          [until]="row.statusUntil"
          [live]="row.live"
        ></app-live-status-duration>
      </small>
    </div>
  } @else {
    <span>—</span>
  }
</ng-template>

<ng-template #dataRecordDurationTemplate let-row>
  <app-live-status-duration
    [changedAt]="row.changedAt"
    [until]="row.statusUntil"
    [live]="row.live"
  ></app-live-status-duration>
</ng-template>

<ng-template #dataRecordChangedTemplate let-row>
  @if (row.changedAt) {
    <span>{{ row.changedAt | dateFormatLang:translate.currentLang:'seconds' }}</span>
  } @else {
    <span>—</span>
  }
</ng-template>

<ng-template #logsTemplate>
      <section class="operation-logs">
        <div class="section-heading log-heading">
          <div class="log-title">
            <h3>{{ 'External logs' | translate }}</h3>
            @if (operationLogsProvider) {
              <p-tag [value]="operationLogsProvider" severity="secondary"></p-tag>
            }
          </div>
          <div class="log-actions">
            @if (operationLogsExternalUrl) {
              <a
                class="external-log-link"
                [href]="operationLogsExternalUrl"
                target="_blank"
                rel="noopener noreferrer">
                {{ 'Open in external log viewer' | translate }}
              </a>
            }
            <app-copyable-log-field
              [text]="operationLogsCopyText"
              [showText]="false"
              copyLabel="Copy log"
            ></app-copyable-log-field>
            <app-secondary-button
              title="Refresh logs"
              icon="pi pi-refresh"
              size="small"
              [disable]="operationLogsLoading"
              (action)="loadOperationLogs()"
            ></app-secondary-button>
          </div>
        </div>

        <div class="log-filters">
          <label>
            <span>{{ 'Log level' | translate }}</span>
            <select [(ngModel)]="operationLogLevel" (ngModelChange)="loadOperationLogs()">
              <option value="">{{ 'All levels' | translate }}</option>
              @for (level of logLevels; track level) {
                <option [value]="level">{{ level | translate }}</option>
              }
            </select>
          </label>
          <label>
            <span>{{ 'Search execution logs' | translate }}</span>
            <input
              type="search"
              [(ngModel)]="operationLogSearch"
              (keyup.enter)="loadOperationLogs()" />
          </label>
          <app-primary-button
            title="Search"
            (action)="loadOperationLogs()"
          ></app-primary-button>
        </div>

        @if (operationLogsLoading) {
          <app-loading [loading]="true"></app-loading>
        } @else if (!operationLogsAvailable) {
          <div class="log-notice">{{ (operationLogsMessage || 'External log search is not configured.') | translate }}</div>
        } @else {
          @if (operationLogsHasMore) {
            <div class="log-notice">{{ 'More logs are available; refine the filters.' | translate }}</div>
          }
          <div class="table-wrapper log-table-wrapper">
            <table class="log-table">
              <thead>
                <tr>
                  <th>{{ 'Time' | translate }}</th>
                  <th>{{ 'Level' | translate }}</th>
                  <th>{{ 'Service' | translate }}</th>
                  <th>{{ 'Message' | translate }}</th>
                </tr>
              </thead>
              <tbody>
                @for (entry of operationLogViews; track (entry.timestamp || '') + (entry.message || '') + $index) {
                  <tr>
                    <td>{{ entry.timestamp | dateFormatLang:translate.currentLang:'ms' }}</td>
                    <td><p-tag [value]="entry.level | translate" [severity]="entry.severity"></p-tag></td>
                    <td>
                      <strong>{{ entry.serviceName }}</strong>
                      @if (entry.loggerName) { <small>{{ entry.loggerName }}</small> }
                    </td>
                    <td class="log-message">
                      <span>{{ entry.message }}</span>
                      @if (entry.traceId) {
                        <small>
                          {{ 'Trace ID' | translate }}:
                          <app-copyable-identifier [value]="entry.traceId"></app-copyable-identifier>
                        </small>
                      }
                      @if (entry.exception) {
                        <app-copyable-log-field
                          [label]="'Exception Details'"
                          [text]="entry.exception"
                        ></app-copyable-log-field>
                      }
                    </td>
                  </tr>
                } @empty {
                  <tr><td colspan="4" class="empty-state compact">{{ 'No logs found for this operation' | translate }}</td></tr>
                }
              </tbody>
            </table>
          </div>
        }
      </section>
</ng-template>

<p-dialog [resizable]="false" [draggable]="false" [(visible)]="restartVisible" [modal]="true" [style]="{ width: 'min(520px, 94vw)' }" [header]="'Restart assignment' | translate">
  <p>{{ 'Restart assignment confirmation' | translate }}</p>
  <ng-template #footer>
    <app-secondary-button
      title="Keep as-is"
      [disable]="restartSaving"
      (action)="restartVisible = false"
    ></app-secondary-button>
    <app-primary-button
      title="Restart assignment"
      icon="pi pi-replay"
      [loading]="restartSaving"
      (action)="confirmRestart()"
    ></app-primary-button>
  </ng-template>
</p-dialog>

<p-dialog [resizable]="false" [draggable]="false" [(visible)]="cancellationVisible" [modal]="true" [style]="{ width: 'min(520px, 94vw)' }" [header]="'Cancel assignment' | translate">
  <p>{{ 'Cancel assignment confirmation' | translate }}</p>
  <ng-template #footer>
    <app-secondary-button
      title="Keep running"
      [disable]="cancellationSaving"
      (action)="cancellationVisible = false"
    ></app-secondary-button>
    <app-primary-button
      title="Cancel assignment"
      icon="pi pi-stop-circle"
      [loading]="cancellationSaving"
      (action)="confirmCancellation()"
    ></app-primary-button>
  </ng-template>
</p-dialog>

<p-dialog [resizable]="false" [draggable]="false" [(visible)]="copyVisible" [modal]="true" [style]="{ width: 'min(520px, 94vw)' }" [header]="'Copy assignment' | translate">
  <p>{{ 'Copy assignment confirmation' | translate }}</p>
  <ng-template #footer>
    <app-secondary-button title="Keep as-is" (action)="copyVisible = false"></app-secondary-button>
    <app-primary-button
      title="Copy assignment"
      icon="pi pi-copy"
      [loading]="copySaving"
      (action)="confirmCopy()"
    ></app-primary-button>
  </ng-template>
</p-dialog>

<app-make-subscription-dialog (failed)="errorMessage = $event"></app-make-subscription-dialog>
</div>
